import { useState, useEffect, useCallback } from "react";
import { useParams } from "wouter";
import { Loader2, Trash2, Plus, Sparkles } from "lucide-react";
import { toast } from "sonner";
import Layout, { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ui/Cards";
import { Button } from "@/components/ui/Buttons";
import { Input, Textarea } from "@/components/ui/Inputs";
import NextStepFooter from "@/components/NextStepFooter";
import AiBrainPanel from "@/components/AiBrainPanel";
import { useProject } from "@/hooks/useProjects";
import { useOrgContext } from "@/hooks/useOrgContext";
import { useClientContext } from "@/hooks/useClientContext";
import { useStrategy, useUpdateStrategy } from "@/hooks/useStrategy";
import { useAiJob } from "@/hooks/useAiJob";
import { api, type Strategy, type StrategyTheme } from "@/lib/api";
import { cn } from "@/lib/utils";

export default function StrategyPage() {
  const params = useParams<{ id?: string }>();
  const projectId = params.id;

  const { data: project } = useProject(projectId ?? null);
  const { data: strategy, isLoading: strategyLoading } = useStrategy(projectId);
  const { data: clientContextData } = useClientContext(projectId);
  const orgContext = useOrgContext();
  const updateStrategy = useUpdateStrategy(projectId ?? "");

  const [isDirty, setIsDirty] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const { data: job } = useAiJob(jobId);

  const [formData, setFormData] = useState({
    positioningStatement: "",
    winThemes: [] as StrategyTheme[],
    creativeIdeas: [] as StrategyTheme[],
    risks: [] as StrategyTheme[],
    additionalNotes: "",
  });

  useEffect(() => {
    if (strategy) {
      setFormData({
        positioningStatement: strategy.positioningStatement || "",
        winThemes: strategy.winThemes || [],
        creativeIdeas: strategy.creativeIdeas || [],
        risks: strategy.risks || [],
        additionalNotes: strategy.additionalNotes || "",
      });
      setIsDirty(false);
    }
  }, [strategy]);

  useEffect(() => {
    if (job?.status === "complete" && jobId) {
      // Refetch strategy to get the newly generated content
      window.location.reload();
      setJobId(null);
      setIsGenerating(false);
    } else if (job?.status === "failed") {
      toast.error(job?.error || "Strategy generation failed");
      setJobId(null);
      setIsGenerating(false);
    }
  }, [job?.status, jobId]);

  const handleGenerateStrategy = async () => {
    if (!projectId) return;
    try {
      setIsGenerating(true);
      const result = await api.generateStrategyJob(projectId);
      setJobId(result.jobId);
    } catch (err) {
      toast.error((err as Error).message ?? "Failed to generate strategy");
      setIsGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!projectId) return;
    try {
      await updateStrategy.mutateAsync({
        positioningStatement: formData.positioningStatement,
        winThemes: formData.winThemes,
        creativeIdeas: formData.creativeIdeas,
        risks: formData.risks,
        additionalNotes: formData.additionalNotes,
      });
      setIsDirty(false);
      toast.success("Strategy saved.");
    } catch (err) {
      toast.error((err as Error).message ?? "Failed to save strategy.");
    }
  };

  const addTheme = (field: "winThemes" | "creativeIdeas" | "risks") => {
    setFormData((prev) => ({
      ...prev,
      [field]: [...prev[field], { title: "", description: "" }],
    }));
    setIsDirty(true);
  };

  const updateTheme = (
    field: "winThemes" | "creativeIdeas" | "risks",
    index: number,
    key: "title" | "description",
    value: string,
  ) => {
    setFormData((prev) => {
      const newArray = [...prev[field]];
      newArray[index] = { ...newArray[index], [key]: value };
      return { ...prev, [field]: newArray };
    });
    setIsDirty(true);
  };

  const removeTheme = (field: "winThemes" | "creativeIdeas" | "risks", index: number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
    setIsDirty(true);
  };

  if (strategyLoading) {
    return (
      <Layout projectId={projectId}>
        <PageHeader title="Strategy" subtitle="Loading…" />
        <div className="flex items-center justify-center py-24">
          <Loader2 size={20} className="animate-spin text-orange-400" />
        </div>
      </Layout>
    );
  }

  const hasStrategy = formData.positioningStatement.length > 0;

  return (
    <Layout projectTitle={project?.title} projectId={projectId}>
      <PageHeader
        title="Strategy"
        subtitle={
          strategy?.generatedAt
            ? `Last generated: ${new Date(strategy.generatedAt).toLocaleDateString()}`
            : ""
        }
      />

      <div className="space-y-6">
        {/* AI Brain Panel */}
        {strategy && (
          <AiBrainPanel
            orgContext={orgContext?.data?.context || null}
            clientContext={clientContextData?.context || null}
            strategy={strategy}
          />
        )}

        {/* Generate panel — quiet style, matches other cards. */}
        <Card>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                style={{ backgroundColor: "rgba(249,115,22,0.1)" }}
              >
                <Sparkles size={16} style={{ color: "#F97316" }} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-white">
                  {hasStrategy ? "Regenerate with AI" : "Let the AI draft a strategy"}
                </h3>
                <p className="text-xs mt-1" style={{ color: "#9CA3AF" }}>
                  {hasStrategy
                    ? "AI proposes an updated strategy using everything gathered so far. Your existing themes will be replaced — save first if you want to keep them."
                    : "AI reads the RFP, your agency profile, the client context, and any past RFPs to propose a positioning, win themes, creative ideas, and risks."}
                </p>
              </div>
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={handleGenerateStrategy}
              disabled={isGenerating}
              icon={
                isGenerating ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Sparkles size={14} />
                )
              }
            >
              {isGenerating ? "Thinking…" : hasStrategy ? "Re-generate" : "Generate"}
            </Button>
          </div>

          {isGenerating && (
            <div className="mt-4 p-4 bg-black bg-opacity-20 rounded border border-orange-600">
              <p className="text-sm text-orange-100">
                AI is thinking… This usually takes 2-4 minutes.
              </p>
              <div className="mt-3 w-full bg-black bg-opacity-40 h-1.5 rounded-full overflow-hidden">
                <div className="h-full bg-orange-500 rounded-full animate-pulse w-1/3" />
              </div>
            </div>
          )}
        </Card>

        {/* Positioning Statement */}
        <Card>
          <div className="space-y-3">
            <label className="block font-semibold text-white text-sm">Positioning Statement</label>
            <Textarea
              value={formData.positioningStatement}
              onChange={(e) => {
                setFormData((prev) => ({
                  ...prev,
                  positioningStatement: e.target.value,
                }));
                setIsDirty(true);
              }}
              placeholder="How should the agency position itself for this RFP?"
              rows={5}
              className="bg-slate-900 border-slate-700 text-white placeholder-slate-500"
            />
            <p className="text-xs text-slate-400">
              3-5 sentences capturing the agency's positioning and how it addresses the client's
              needs.
            </p>
          </div>
        </Card>

        {/* Win Themes */}
        <Card>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="font-semibold text-white text-sm">Win Themes</label>
              <Button
                onClick={() => addTheme("winThemes")}
                variant="ghost"
                size="sm"
                className="text-orange-400 hover:bg-orange-950"
              >
                <Plus size={14} className="mr-1" />
                Add Theme
              </Button>
            </div>

            <div className="space-y-3">
              {formData.winThemes.map((theme, idx) => (
                <div
                  key={idx}
                  className="space-y-2 p-3 bg-slate-900 rounded border border-slate-700"
                >
                  <Input
                    value={theme.title}
                    onChange={(e) => updateTheme("winThemes", idx, "title", e.target.value)}
                    placeholder="Theme title (e.g., Deep Client Relationships)"
                    className="bg-slate-800 border-slate-600 text-white placeholder-slate-500 text-sm"
                  />
                  <Textarea
                    value={theme.description}
                    onChange={(e) => updateTheme("winThemes", idx, "description", e.target.value)}
                    placeholder="Why this is a winning angle for the agency"
                    rows={2}
                    className="bg-slate-800 border-slate-600 text-white placeholder-slate-500 text-sm"
                  />
                  <div className="flex justify-end">
                    <Button
                      onClick={() => removeTheme("winThemes", idx)}
                      variant="ghost"
                      size="sm"
                      className="text-red-400 hover:bg-red-950"
                    >
                      <Trash2 size={14} className="mr-1" />
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-400">
              3-5 competitive angles the agency uniquely owns.
            </p>
          </div>
        </Card>

        {/* Creative Ideas */}
        <Card>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="font-semibold text-white text-sm">Creative Ideas</label>
              <Button
                onClick={() => addTheme("creativeIdeas")}
                variant="ghost"
                size="sm"
                className="text-orange-400 hover:bg-orange-950"
              >
                <Plus size={14} className="mr-1" />
                Add Idea
              </Button>
            </div>

            <div className="space-y-3">
              {formData.creativeIdeas.map((idea, idx) => (
                <div
                  key={idx}
                  className="space-y-2 p-3 bg-slate-900 rounded border border-slate-700"
                >
                  <Input
                    value={idea.title}
                    onChange={(e) => updateTheme("creativeIdeas", idx, "title", e.target.value)}
                    placeholder="Creative idea title"
                    className="bg-slate-800 border-slate-600 text-white placeholder-slate-500 text-sm"
                  />
                  <Textarea
                    value={idea.description}
                    onChange={(e) =>
                      updateTheme("creativeIdeas", idx, "description", e.target.value)
                    }
                    placeholder="Concrete details of the idea"
                    rows={2}
                    className="bg-slate-800 border-slate-600 text-white placeholder-slate-500 text-sm"
                  />
                  <div className="flex justify-end">
                    <Button
                      onClick={() => removeTheme("creativeIdeas", idx)}
                      variant="ghost"
                      size="sm"
                      className="text-red-400 hover:bg-red-950"
                    >
                      <Trash2 size={14} className="mr-1" />
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-400">
              Up to 5 specific, executable ideas worth proposing.
            </p>
          </div>
        </Card>

        {/* Risks */}
        <Card>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="font-semibold text-white text-sm">Risks and Mitigations</label>
              <Button
                onClick={() => addTheme("risks")}
                variant="ghost"
                size="sm"
                className="text-orange-400 hover:bg-orange-950"
              >
                <Plus size={14} className="mr-1" />
                Add Risk
              </Button>
            </div>

            <div className="space-y-3">
              {formData.risks.map((risk, idx) => (
                <div
                  key={idx}
                  className="space-y-2 p-3 bg-slate-900 rounded border border-slate-700"
                >
                  <Input
                    value={risk.title}
                    onChange={(e) => updateTheme("risks", idx, "title", e.target.value)}
                    placeholder="Risk type (e.g., Budget constraints)"
                    className="bg-slate-800 border-slate-600 text-white placeholder-slate-500 text-sm"
                  />
                  <Textarea
                    value={risk.description}
                    onChange={(e) => updateTheme("risks", idx, "description", e.target.value)}
                    placeholder="The risk and a brief mitigation"
                    rows={2}
                    className="bg-slate-800 border-slate-600 text-white placeholder-slate-500 text-sm"
                  />
                  <div className="flex justify-end">
                    <Button
                      onClick={() => removeTheme("risks", idx)}
                      variant="ghost"
                      size="sm"
                      className="text-red-400 hover:bg-red-950"
                    >
                      <Trash2 size={14} className="mr-1" />
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-400">
              Up to 4 real risks (competitive, operational, or compliance) with mitigation.
            </p>
          </div>
        </Card>

        {/* Additional Notes */}
        <Card>
          <div className="space-y-3">
            <label className="block font-semibold text-white text-sm">Additional Notes</label>
            <Textarea
              value={formData.additionalNotes}
              onChange={(e) => {
                setFormData((prev) => ({
                  ...prev,
                  additionalNotes: e.target.value,
                }));
                setIsDirty(true);
              }}
              placeholder="Any other strategic considerations…"
              rows={4}
              className="bg-slate-900 border-slate-700 text-white placeholder-slate-500"
            />
          </div>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={!isDirty || updateStrategy.isPending}
            className="bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50"
          >
            {updateStrategy.isPending ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                Saving…
              </>
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </div>

      {projectId && <NextStepFooter projectId={projectId} currentStep="strategy" />}
    </Layout>
  );
}
